import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { api, type Department } from '../../lib/api';
import { DEPARTMENT_COLORS } from '../../lib/categories';
import { useDepartmentStore } from '../../store/departmentStore';
import { ConfirmDialog } from '../ConfirmDialog';

function DepartmentDialog({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: Department | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(DEPARTMENT_COLORS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const input = { name, description, color };
      if (editing) await api.admin.updateDepartment(editing.id, input);
      else await api.admin.createDepartment(input);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the department');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="department-dialog-title"
      slotProps={{
        transition: {
          onEnter: () => {
            setName(editing?.name ?? '');
            setDescription(editing?.description ?? '');
            setColor(editing?.color ?? DEPARTMENT_COLORS[0]);
            setError(null);
          },
        },
      }}
    >
      <DialogTitle id="department-dialog-title" component="h2" variant="h6">
        {editing ? 'Edit department' : 'Add department'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" autoFocus fullWidth value={name} onChange={(e) => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 60 } }} />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Shown on the folder card"
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <Box>
            <FormLabel component="div" sx={{ mb: 1 }}>
              Folder colour
            </FormLabel>
            <Stack direction="row" spacing={1}>
              {DEPARTMENT_COLORS.map((c) => (
                <ButtonBase
                  key={c}
                  aria-label={`Colour ${c}`}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  sx={(t) => ({
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: c,
                    border: color === c ? `2px solid ${t.palette.background.paper}` : `1px solid ${t.palette.chrome.border}`,
                    boxShadow: color === c ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                  })}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={() => void save()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {editing ? 'Save' : 'Add department'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DepartmentsTab() {
  const departments = useDepartmentStore((s) => s.departments);
  const refresh = useDepartmentStore((s) => s.refresh);
  const [dialog, setDialog] = useState<{ open: boolean; editing: Department | null }>({ open: false, editing: null });
  const [toDelete, setToDelete] = useState<Department | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Department folders organise shared decks and announcements on the portal.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, editing: null })}>
          Add department
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {departments.map((d, i) => (
          <Stack
            key={d.id}
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center', px: 2, py: 1.25, borderTop: i ? 1 : 0, borderColor: 'divider' }}
          >
            <FolderOutlinedIcon sx={{ color: d.color }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {d.name}
              </Typography>
              <Typography variant="caption" noWrap component="div">
                {d.description || 'No description'}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              {d.deck_count} decks · {d.announcement_count} announcements
            </Typography>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label={`Edit ${d.name}`} onClick={() => setDialog({ open: true, editing: d })}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={d.id === 'general' ? 'General is the default folder' : 'Delete'}>
              <span>
                <IconButton size="small" aria-label={`Delete ${d.name}`} disabled={d.id === 'general'} onClick={() => setToDelete(d)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
      </Box>

      <DepartmentDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => void refresh()}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        title={`Delete ${toDelete?.name ?? ''}?`}
        message="The folder is removed. Its decks and announcements move to General; nothing is deleted."
        confirmLabel="Delete department"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const d = toDelete;
          if (d)
            void api.admin
              .deleteDepartment(d.id)
              .then(refresh)
              .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete'));
        }}
      />
    </Stack>
  );
}
